import * as Cesium from 'cesium';
import * as THREE from 'three';

export interface GeoAnchor {
  lat: number;
  lon: number;
}

export interface BuildingGeoPlacement {
  resolutionKey: string;
  east: number;
  north: number;
  up: number;
  /** Position in the same local ENU frame Three.js uses for building groups. */
  x: number;
  y: number;
  z: number;
  /** Max horizontal distance from local center to footprint vertices (meters). */
  footprintExtentMeters: number;
}

export function calculateBuildingCenterFromPerimeters(floorPerimeters: any[]): GeoAnchor {
  let minLat = 90;
  let maxLat = -90;
  let minLon = 180;
  let maxLon = -180;
  let coordCount = 0;

  floorPerimeters.forEach((area) => {
    const locations = area?.locations;
    if (!Array.isArray(locations)) {
      return;
    }

    for (const coord of locations) {
      if (!Array.isArray(coord) || coord.length < 2) {
        continue;
      }

      const [lat, lon] = coord;
      if (
        typeof lon === 'number' &&
        typeof lat === 'number' &&
        !isNaN(lon) &&
        !isNaN(lat)
      ) {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
        coordCount++;
      }
    }
  });

  if (coordCount === 0 || minLat === 90 || maxLat === -90 || minLon === 180 || maxLon === -180) {
    return { lat: 0, lon: 0 };
  }

  return {
    lat: (minLat + maxLat) / 2,
    lon: (minLon + maxLon) / 2,
  };
}

export function computeBuildingFootprintExtentMeters(
  building: any,
  viewer?: Cesium.Viewer | null
): number {
  const localCenter = resolveBuildingLocalCenter(building);
  if (!localCenter) {
    return 0;
  }

  const floors = building?.getFloorPerimeters?.() ?? building?.floorPerimeters;
  if (!Array.isArray(floors)) {
    return 0;
  }

  let maxDist = 0;
  floors.forEach((floor: any) => {
    if (floor?.isFloorPerimeter === false || !Array.isArray(floor?.locations)) {
      return;
    }
    floor.locations.forEach((coord: number[]) => {
      if (!Array.isArray(coord) || coord.length < 2) {
        return;
      }
      const [lat, lon] = coord;
      if (typeof lat !== 'number' || typeof lon !== 'number') {
        return;
      }
      const offset = geoOffsetMetersFromAnchor(localCenter, { lat, lon }, viewer);
      const dist = Math.hypot(offset.east, offset.north);
      maxDist = Math.max(maxDist, dist);
    });
  });

  return maxDist;
}

export function resolveBuildingLocalCenter(building: any): GeoAnchor | null {
  const pin = building?.pin?.centerpoint;
  if (typeof pin?.latitude === 'number' && typeof pin?.longitude === 'number') {
    return { lat: pin.latitude, lon: pin.longitude };
  }

  const floors = building?.getFloorPerimeters?.() ?? building?.floorPerimeters;
  if (Array.isArray(floors) && floors.length) {
    const floorAreas = floors.filter(
      (floor: any) => floor?.isFloorPerimeter !== false && Array.isArray(floor?.locations)
    );
    if (floorAreas.length) {
      return calculateBuildingCenterFromPerimeters(floorAreas);
    }
  }

  return null;
}

export function resolveCampusGeographicAnchor(
  buildings: any[],
  viewer?: Cesium.Viewer | null
): GeoAnchor | null {
  if (!buildings?.length) {
    return null;
  }

  const anchorBuilding =
    buildings.find((building) => typeof building?.pin?.centerpoint?.latitude === 'number') ??
    buildings[0];

  const pinCenter = resolveBuildingLocalCenter(anchorBuilding);
  if (pinCenter && pinCenter.lat !== 0 && pinCenter.lon !== 0) {
    return pinCenter;
  }

  const floors = anchorBuilding?.getFloorPerimeters?.() ?? anchorBuilding?.floorPerimeters;
  if (Array.isArray(floors) && floors.length) {
    return calculateBuildingCenterFromPerimeters(floors);
  }

  void viewer;
  return null;
}

export function getTerrainHeightAtAnchor(
  anchor: GeoAnchor,
  viewer?: Cesium.Viewer | null
): number {
  if (!viewer) {
    return 0;
  }

  const cartographic = Cesium.Cartographic.fromDegrees(anchor.lon, anchor.lat);
  return viewer.scene.globe.getHeight(cartographic) || 0;
}

/** Same ENU -> Three.js matrix remap used by ThreeIntegrationService building groups. */
export function computeThreeBuildingMatrixFromAnchor(
  anchor: GeoAnchor,
  localCenter: GeoAnchor,
  viewer?: Cesium.Viewer | null
): THREE.Matrix4 {
  const groundHeight = getTerrainHeightAtAnchor(anchor, viewer);
  const centerCartesian = Cesium.Cartesian3.fromDegrees(anchor.lon, anchor.lat, groundHeight);
  const targetCartesian = Cesium.Cartesian3.fromDegrees(
    localCenter.lon,
    localCenter.lat,
    groundHeight
  );
  const centerEnu = Cesium.Transforms.eastNorthUpToFixedFrame(centerCartesian);
  const targetEnu = Cesium.Transforms.eastNorthUpToFixedFrame(targetCartesian);
  const invCenterEnu = Cesium.Matrix4.inverse(centerEnu, new Cesium.Matrix4());
  const transformCesium = Cesium.Matrix4.multiply(invCenterEnu, targetEnu, new Cesium.Matrix4());
  const m = Cesium.Matrix4.toArray(transformCesium);
  const matrix = new THREE.Matrix4();
  matrix.set(
    m[0], m[8], -m[4], m[12],
    m[2], m[10], -m[6], m[14],
    -m[1], -m[9], m[5], -m[13],
    0, 0, 0, 1
  );
  return matrix;
}

export function computeThreeBuildingPositionFromAnchor(
  anchor: GeoAnchor,
  localCenter: GeoAnchor,
  viewer?: Cesium.Viewer | null
): { x: number; y: number; z: number } {
  const matrix = computeThreeBuildingMatrixFromAnchor(anchor, localCenter, viewer);
  const position = new THREE.Vector3();
  position.setFromMatrixPosition(matrix);
  return { x: position.x, y: position.y, z: position.z };
}

export function geoOffsetMetersFromAnchor(
  anchor: GeoAnchor,
  target: GeoAnchor,
  viewer?: Cesium.Viewer | null
): { east: number; north: number; up: number } {
  const groundHeight = getTerrainHeightAtAnchor(anchor, viewer);
  const centerCartesian = Cesium.Cartesian3.fromDegrees(anchor.lon, anchor.lat, groundHeight);
  const targetCartesian = Cesium.Cartesian3.fromDegrees(target.lon, target.lat, groundHeight);
  const enu = Cesium.Transforms.eastNorthUpToFixedFrame(centerCartesian);
  const invEnu = Cesium.Matrix4.inverse(enu, new Cesium.Matrix4());
  const offset = Cesium.Cartesian3.subtract(targetCartesian, centerCartesian, new Cesium.Cartesian3());
  const local = Cesium.Matrix4.multiplyByPointAsVector(invEnu, offset, new Cesium.Cartesian3());
  return { east: local.x, north: local.y, up: local.z };
}

export function computeBuildingGeoPlacement(
  building: any,
  anchor: GeoAnchor,
  viewer?: Cesium.Viewer | null,
  resolutionKey?: string
): BuildingGeoPlacement | null {
  const localCenter = resolveBuildingLocalCenter(building);
  if (!localCenter) {
    return null;
  }

  const key = resolutionKey ?? String(building?.perimeterId ?? building?.id ?? '');
  if (!key) {
    return null;
  }

  const offset = geoOffsetMetersFromAnchor(anchor, localCenter, viewer);
  const position = computeThreeBuildingPositionFromAnchor(anchor, localCenter, viewer);
  const footprintExtentMeters = computeBuildingFootprintExtentMeters(building, viewer);

  return {
    resolutionKey: key,
    east: offset.east,
    north: offset.north,
    up: offset.up,
    x: position.x,
    y: position.y,
    z: position.z,
    footprintExtentMeters,
  };
}

export function computeBuildingGeoPlacements(
  buildings: any[],
  anchor: GeoAnchor,
  viewer?: Cesium.Viewer | null
): BuildingGeoPlacement[] {
  return buildings
    .map((building) =>
      computeBuildingGeoPlacement(
        building,
        anchor,
        viewer,
        String(building?.perimeterId ?? building?.id ?? '')
      )
    )
    .filter((placement): placement is BuildingGeoPlacement => !!placement);
}
